import { useState, useEffect, useCallback, useRef } from "react";
import API, { formatApiError } from "../../lib/api";
import { compressImage } from "../../lib/imageCompression";
import AuthImage from "../AuthImage";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { ArrowsLeftRight, UploadSimple, HandTap, CheckCircle } from "@phosphor-icons/react";
import { PairCard, PairViewer, MarkerList } from "./PairCard";

function BeforeTile({ b, active, dropping, onPick, onDrop, onDragOver, onDragLeave }) {
  return (
    <button type="button" onClick={onPick} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
      className={`relative overflow-hidden rounded-md border-2 text-left transition-colors ${dropping ? "border-emerald-500 ring-2 ring-emerald-300" : active ? "border-primary animate-pulse" : "border-border hover:border-primary/60"}`}
      data-testid={`compare-before-${b.key}`} title={active ? "Tap to pair the selected completion photo with this original" : b.name}>
      <div className="aspect-[4/3]"><AuthImage path={b.url} alt={b.name} className="h-full w-full object-cover" loading="lazy" /></div>
      <span className="absolute left-0 top-0 rounded-br-md bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">{b.ai_markup ? "Marked-up" : "Original"}</span>
      {b.paired_count > 0 && <span className="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-semibold text-white" data-testid={`compare-before-${b.key}-paired`}><CheckCircle size={10} weight="fill" /> {b.paired_count}</span>}
      <div className="p-1.5"><MarkerList markers={(b.markers || []).slice(0, 3)} />{!b.markers?.length && <p className="truncate text-[10px] text-muted-foreground">{b.name}</p>}</div>
    </button>
  );
}

function AfterTile({ a, selected, onSelect, onDragStart }) {
  return (
    <button type="button" draggable onDragStart={onDragStart} onClick={onSelect}
      className={`relative aspect-square overflow-hidden rounded-md border-2 transition-colors ${selected ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/60"}`}
      data-testid={`compare-after-${a.id}`} title="Drag onto an original — or tap, then tap the original">
      <AuthImage path={a.url} alt={a.name} className="h-full w-full object-cover" loading="lazy" />
      {selected && <span className="absolute inset-x-0 bottom-0 bg-primary/90 py-0.5 text-center text-[9px] font-semibold text-primary-foreground">Now tap the original</span>}
    </button>
  );
}

export default function BeforeAfterCompare({ entityType, entityId, canEdit = true }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dropping, setDropping] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);
  const base = `/photo-compare/${entityType}/${entityId}`;

  const load = useCallback(() => API.get(base).then((r) => setData(r.data)).catch(() => setData({ before: [], after: [], pairs: [] })), [base]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const pairs = data.pairs || [], before = data.before || [], unpairedAfter = (data.after || []).filter((a) => !a.paired);
  if (!canEdit && !pairs.length) return null;

  const pairUp = async (beforeKey, afterId) => {
    if (!beforeKey || !afterId) return;
    try {
      const { data: d } = await API.post(`${base}/pairs`, { before_key: beforeKey, after_id: afterId });
      setData(d); setSelected(null); setDropping(null);
      toast.success("Paired — the client and the completion report will show this comparison");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not pair the photos"); }
  };
  const unpair = async (pairId) => {
    try { const { data: d } = await API.delete(`${base}/pairs/${pairId}`); setData(d); toast.success("Unpaired"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not unpair"); }
  };
  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", await compressImage(f), f.name);
      const { data: d } = await API.post(`${base}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData(d);
      toast.success(`${files.length} completion photo${files.length !== 1 ? "s" : ""} added — now pair ${files.length !== 1 ? "them" : "it"} with the original`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="before-after-compare">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold"><ArrowsLeftRight className="h-5 w-5" /> Before &amp; after <span className="text-xs font-normal text-muted-foreground">({pairs.length} paired)</span></h2>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="compare-upload-input" />
            <Button size="sm" variant="outline" className="gap-1" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="compare-upload-btn"><UploadSimple size={14} /> {uploading ? "Uploading…" : "Upload completion photos"}</Button>
          </>
        )}
      </div>
      {canEdit && <p className="mb-3 text-xs text-muted-foreground">Upload your completion photos, then pair each one with the marked-up original it shows. Paired photos appear side by side for the office, the client and on the completion report.</p>}

      {pairs.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2" data-testid="compare-pairs">
          {pairs.map((p) => <PairCard key={p.id} pair={p} onOpen={() => setViewing(p)} onUnpair={canEdit ? () => unpair(p.id) : undefined} />)}
        </div>
      )}

      {canEdit && before.length === 0 && pairs.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground" data-testid="compare-empty">No marked-up photos on this job yet. Ask the office to run “Mark up photos with AI” on the request and the originals will appear here to pair against.</p>
      )}

      {canEdit && before.length > 0 && (
        <div className={`${pairs.length ? "mt-4 border-t border-border pt-4" : ""}`} data-testid="compare-pairing">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pair up completion photos</p>
          <p className="mb-3 flex items-center gap-1 text-xs text-muted-foreground"><HandTap size={14} /> Drag a completion photo onto the original it shows — or tap the completion photo, then tap the original.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-[11px] font-medium text-red-700 dark:text-red-400">Originals ({before.length})</p>
              <div className="grid grid-cols-2 gap-2">
                {before.map((b) => (
                  <BeforeTile key={b.key} b={b} active={!!selected} dropping={dropping === b.key}
                    onPick={() => selected && pairUp(b.key, selected)}
                    onDragOver={(e) => { e.preventDefault(); setDropping(b.key); }} onDragLeave={() => setDropping(null)}
                    onDrop={(e) => { e.preventDefault(); pairUp(b.key, e.dataTransfer.getData("text/plain")); }} />
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">Completion photos to pair ({unpairedAfter.length})</p>
              {unpairedAfter.length === 0 ? (
                <button type="button" onClick={() => fileRef.current?.click()} className="flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border p-6 text-xs text-muted-foreground hover:border-primary hover:text-foreground" data-testid="compare-after-empty">
                  <UploadSimple size={18} /> {data.after?.length ? "Every completion photo is paired — upload more" : "Upload completion photos to start"}
                </button>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {unpairedAfter.map((a) => <AfterTile key={a.id} a={a} selected={selected === a.id} onSelect={() => setSelected(selected === a.id ? null : a.id)} onDragStart={(e) => { e.dataTransfer.setData("text/plain", a.id); e.dataTransfer.effectAllowed = "link"; }} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {viewing && <PairViewer pair={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
